import React, { useState, useEffect } from 'react';
import ImageGalleryModal from './ImageGalleryModal';
import { textures } from '../data/texture';
import { request } from '../utils/request';

// 图片类型定义
interface ImageItem {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  type: string;
  isPublic: boolean;
  views: number;
  downloads: number;
  url?: string;
}

// 从接口加载背景图片数据
const loadBackgroundImagesFromAPI = async (): Promise<ImageItem[]> => {
  try {
    const response: any = await request({
      url: 'images/list',
      method: 'GET',
      params: { type: 'background' }
    });
    
    if (response.success && response.result) {
      return response.result.map((item: any) => ({
        id: item.id,
        name: item.name,
        size: item.size,
        uploadDate: item.uploadDate,
        type: item.type,
        isPublic: item.isPublic,
        views: item.views,
        downloads: item.downloads,
        url: item.url
      }));
    }
    return [];
  } catch (error) {
    console.error('Error loading background images from API:', error);
    return [];
  }
};

interface BackgroundSectionProps {
  product: any;
  selectedColor: string;
  selectedColorObj: any;
  onSelectedColorChange: (colorId: string) => void;
  onSetBackgroundTexture: (texture: string) => void;
  onSetBackgroundImage: (imageUrl: string) => void;
  onUploadBackgroundImage: (file: File) => void;
  onSetBackgroundColor: (color: string) => void;
}

const BackgroundSection: React.FC<BackgroundSectionProps> = ({
  product,
  selectedColor,
  selectedColorObj,
  onSelectedColorChange,
  onSetBackgroundTexture,
  onSetBackgroundImage,
  onUploadBackgroundImage,
  onSetBackgroundColor
}) => {
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [selectedTexture, setSelectedTexture] = useState('');
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const [backgroundImages, setBackgroundImages] = useState<ImageItem[]>([]);
  const [isLoadingBackgroundImages, setIsLoadingBackgroundImages] = useState(false);

  // 加载背景图片数据
  useEffect(() => {
    const loadBackgroundImages = async () => {
      setIsLoadingBackgroundImages(true);
      try {
        const imagesData = await loadBackgroundImagesFromAPI();
        setBackgroundImages(imagesData);
      } catch (error) {
        console.error('Failed to load background images:', error);
      } finally {
        setIsLoadingBackgroundImages(false);
      }
    };

    loadBackgroundImages();
  }, []);

  const sampleImages = [
    'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop&crop=center'
  ];

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Background</h3>
      
      <div className="space-y-6">
        {/* Color Selection */}
        <div>
          <label className="block text-sm font-medium mb-3 text-gray-700">Background Color</label>
          <div className="grid grid-cols-4 gap-3">
            {product?.colors.map((color: any) => (
              <button
                key={color.id}
                onClick={() => onSelectedColorChange(color.id)}
                className={`w-12 h-12 rounded-full border-2 transition-all ${
                  selectedColor === color.id 
                    ? 'scale-110 shadow-lg' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                style={{
                  backgroundColor: color.hex,
                  ...(selectedColor === color.id ? { borderColor: '#2e3a47' } : {})
                }}
                title={color.name}
              />
            ))}
          </div>
        </div>
        
        {/* Custom Color Picker */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700">Custom Color</label>
          <input
            type="color"
            value={selectedColorObj?.hex || '#ffffff'}
            onChange={(e) => {
              // Apply custom color immediately to canvas background
              const newColor = e.target.value;
              onSetBackgroundColor(newColor);
            }}
            className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
          />
        </div>
        
        {/* Background Textures */}
        <div>
          <h4 className="text-md font-medium mb-3 text-gray-700">Background Textures</h4>
          <div className="grid grid-cols-2 gap-3">
            {textures.map((texture) => (
              <div
                key={texture.id}
                className={`h-12 bg-white border rounded cursor-pointer transition-colors ${
                  selectedTexture === texture.id 
                    ? '' 
                    : 'border-gray-300'
                }`}
                style={selectedTexture === texture.id ? { borderColor: '#68aeb0' } : {}}
                onMouseEnter={(e) => {
                  if (selectedTexture !== texture.id) {
                    e.currentTarget.style.borderColor = '#68aeb0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedTexture !== texture.id) {
                    e.currentTarget.style.borderColor = '';
                  }
                }}
                onClick={() => {
                  if (selectedTexture === texture.id) {
                    // If clicking the same texture, remove it
                    setSelectedTexture('');
                    onSetBackgroundTexture('');
                  } else {
                    // If clicking a different texture, apply it
                    setSelectedTexture(texture.id);
                    onSetBackgroundTexture(texture.id);
                  }
                }}
              >
                <div className="w-full h-full bg-repeat" style={texture.style}></div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Background Images */}
        <div>
          <h4 className="text-md font-medium mb-3 text-gray-700">Background Images</h4>
          <div className="space-y-3">
          
            {/* Upload Background Image Button */}
            <button
              onClick={() => setIsImageGalleryOpen(true)}
              className="w-full px-4 py-3 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
              style={{ backgroundColor: '#68aeb0' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Upload Background Image</span>
            </button>
      
            {/* Background Images */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Background Images</label>
              {isLoadingBackgroundImages ? (
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="h-20 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : backgroundImages.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {backgroundImages.slice(0, 4).map((image) => (
                    <div
                      key={image.id}
                      className={`h-20 bg-white border rounded cursor-pointer transition-colors overflow-hidden ${
                        backgroundImageUrl === image.url 
                          ? '' 
                          : 'border-gray-300'
                      }`}
                      style={backgroundImageUrl === image.url ? { borderColor: '#68aeb0' } : {}}
                      onMouseEnter={(e) => {
                        if (backgroundImageUrl !== image.url) {
                          e.currentTarget.style.borderColor = '#68aeb0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (backgroundImageUrl !== image.url) {
                          e.currentTarget.style.borderColor = '';
                        }
                      }}
                      onClick={() => {
                        if (backgroundImageUrl === image.url) {
                          // If clicking the same image, remove it
                          setBackgroundImageUrl('');
                          onSetBackgroundImage('');
                        } else {
                          // If clicking a different image, apply it
                          setBackgroundImageUrl(image.url || '');
                          onSetBackgroundImage(image.url || '');
                        }
                      }}
                    >
                      {image.url ? (
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full bg-gray-200 flex items-center justify-center ${image.url ? 'hidden' : ''}`}>
                        <span className="text-gray-400 text-xs">No Image</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No background images available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Gallery Modal */}
      <ImageGalleryModal
        isOpen={isImageGalleryOpen}
        onClose={() => setIsImageGalleryOpen(false)}
        onSelectImage={(imageUrl) => {
          onSetBackgroundImage(imageUrl);
          setIsImageGalleryOpen(false);
        }}
        onUploadImage={(file) => {
          onUploadBackgroundImage(file);
        }}
      />
    </div>
  );
};

export default BackgroundSection; 